import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { ROUTES } from '../../common/constants/routes';
import MainNavigation from '../../common/main-navigation/MainNavigation';
import AppBar from '../../common/app-bar/AppBar';
import { useAuth } from '@clerk/clerk-react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { LOCATION_EXCEPTIONS } from '../../common/constants/location_exceptions';
import userflow from 'userflow.js';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { RootState } from '../../store';
import MessageModal from '../../common/message-modal/MessageModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hideMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';

// These two credentials shouldn't be exposed, using them from env doesn't work currently
if (process.env.USERFLOW_ENABLED === 'TRUE') {
  userflow.init(process.env.USERFLOW_TOKEN);
  userflow.identify(process.env.USERFLOW_USER_ID);
}

const Layout = (): JSX.Element => {
  const { documentId } = useParams();
  const [tokenLoaded, setTokenLoaded] = useState<boolean>(false);
  const [pendingStatus, setPendingStatus] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const showPlanUpgradingMessage = useAppSelector(
    (state: RootState) => state.showPlanUpgradingMessage.showMessage
  );
  const planUpgradingMessageText = useAppSelector(
    (state: RootState) => state.showPlanUpgradingMessage.messageText
  );
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const locationIncludeExceptionUrl = LOCATION_EXCEPTIONS.some((el) => {
    return location.pathname.includes(el);
  });
  const locationWithFullWidth = [
    ROUTES.SIGNING,
    ROUTES.DOCUMENTS,
    ROUTES.DOCUMENT_VAULT_CATEGORY_DOCUMENT,
  ];
  const locationWithFullWidthUrl = locationWithFullWidth.some((el) => {
    return location.pathname.includes(el);
  });

  useEffect(() => {
    if (isSignedIn && isLoaded) {
      setTokenLoaded(true);
    }
  }, [isSignedIn, isLoaded]);

  useEffect(() => {
    if (locationIncludeExceptionUrl) {
      setPendingStatus(false);
    } else if (!locationIncludeExceptionUrl && tokenLoaded) {
      setPendingStatus(false);
      localStorage.setItem('doc_latest_version', 'true');
    }
  }, [locationIncludeExceptionUrl, tokenLoaded]);

  const renderedLayout = () => {
    return (
      <React.Fragment>
        {pendingStatus ? (
          <ProgressOverlay />
        ) : (
          <Box
            className={
              'layout updated-variant' +
              (location.pathname === ROUTES.PRICING ? ' pricing-page' : '')
            }
          >
            <MainNavigation />
            <Box sx={{ width: '100%' }}>
              <AppBar />
              <Container maxWidth={locationWithFullWidthUrl || documentId ? false : 'lg'}>
                <Outlet />
              </Container>
              {showPlanUpgradingMessage && (
                <MessageModal
                  open={showPlanUpgradingMessage}
                  modalMessage={planUpgradingMessageText || t('messages.monthLimitReached')}
                  actionButtonText={t('buttons.ok')}
                  actionButtonFn={() => {
                    navigate(ROUTES.PRICING);
                    dispatch(hideMessage());
                  }}
                  goBackButtonText={t('buttons.later')}
                  onClose={() => {
                    dispatch(hideMessage());
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </React.Fragment>
    );
  };

  if (!tokenLoaded) return <ProgressOverlay />;

  return renderedLayout();
};
export default Layout;
